/**
 * Generic ingestion API — CSV/sheet-link upload, column mapping, upload
 * history, saved mapping templates, and manual spend entry. Wave G1 Sonnet
 * S4 (docs/plans/gtm-platform/WAVE-G1-DESIGN.md §5/§9).
 *
 * Two response shapes need special handling beyond the usual
 * createHeaders()/JSON pattern the other *Api.ts files use:
 *
 * 1. Multipart bodies (file upload, mapping submit) must NOT set
 *    Content-Type — the browser has to compute the multipart boundary
 *    itself. authHeaders() below carries only the bearer token.
 * 2. The mapping-time validator fails closed with HTTP 422 and a
 *    row-addressed errors array (design §5) — that is expected, renderable
 *    data, not a transport failure. IngestionValidationError carries the
 *    array through the normal throw/catch path so callers can branch on
 *    `instanceof` without re-parsing response bodies.
 */

import { createHeaders, getAuthToken } from './apiClient';
import { handleResponse } from './httpResponse';
import type {
  MapLanded,
  ManualSpendEntryRecord,
  ManualSpendInput,
  ManualSpendListResponse,
  RowError,
  TargetKind,
  UploadCreated,
  UploadMappingsListResponse,
  UploadsListResponse,
} from '../types/dataUploads';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/** Auth-only headers for multipart/form-data bodies — no Content-Type, so
 * the browser fills in `multipart/form-data; boundary=...` itself. */
function authHeaders(): HeadersInit {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Row-addressed mapping-time validation failure (HTTP 422, design §5).
 * Thrown instead of returned so every call site can just try/catch like
 * every other service function, while still carrying the full errors
 * array (not just a flattened message) for row-addressed rendering. */
export class IngestionValidationError extends Error {
  errors: RowError[];

  constructor(errors: RowError[]) {
    super(
      errors.length === 1
        ? `Row ${errors[0].row_number}: ${errors[0].message}`
        : `${errors.length} rows failed validation`
    );
    this.name = 'IngestionValidationError';
    this.errors = errors;
  }
}

/** Same as handleResponse, but a 422 with a row-addressed errors array
 * (mapping submit / manual-spend create-or-update) raises
 * IngestionValidationError instead of a flat message — nothing lands on
 * any error, and the errors are the product, not an afterthought. */
async function handleIngestionResponse<T>(response: Response): Promise<T> {
  if (response.status === 422) {
    const body = await response.json().catch(() => ({ errors: [] as RowError[] }));
    throw new IngestionValidationError(Array.isArray(body.errors) ? body.errors : []);
  }
  return handleResponse<T>(response);
}

// =============================================================================
// Upload intake (POST /api/uploads — CSV multipart or sheet-link JSON)
// =============================================================================

export async function createCsvUpload(file: File): Promise<UploadCreated> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(`${API_BASE_URL}/api/uploads`, {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });
  return handleIngestionResponse<UploadCreated>(response);
}

export async function createSheetLinkUpload(
  sheetUrl: string,
  refreshIntervalMinutes?: number
): Promise<UploadCreated> {
  const response = await fetch(`${API_BASE_URL}/api/uploads`, {
    method: 'POST',
    headers: createHeaders(),
    body: JSON.stringify({
      sheet_url: sheetUrl,
      ...(refreshIntervalMinutes != null
        ? { refresh_interval_minutes: refreshIntervalMinutes }
        : {}),
    }),
  });
  return handleIngestionResponse<UploadCreated>(response);
}

// =============================================================================
// Mapping submit (POST /api/uploads/{id}/map)
// =============================================================================

export interface MapUploadParams {
  uploadId: string;
  /** The CSV File to resend (the API re-derives rows from it rather than
   * trusting stored state). Omitted for a sheet-link upload, whose bytes
   * live behind the stored source URL rather than a local File object —
   * the server re-fetches that source itself. */
  file?: File | null;
  targetKind: TargetKind;
  /** {csv_column: target_field} — only columns actually mapped. */
  columnMap: Record<string, string>;
  /** {target_field: fixed_value} — e.g. one platform label for the file. */
  defaults: Record<string, unknown>;
  /** Name to save this mapping as a reusable per-tenant template. */
  saveAsTemplate?: string;
}

export async function mapUpload(params: MapUploadParams): Promise<MapLanded> {
  const formData = new FormData();
  if (params.file) {
    formData.append('file', params.file);
  }
  formData.append('target_kind', params.targetKind);
  formData.append('column_map', JSON.stringify(params.columnMap));
  formData.append('defaults', JSON.stringify(params.defaults));
  if (params.saveAsTemplate) {
    formData.append('save_as_template', params.saveAsTemplate);
  }
  const response = await fetch(
    `${API_BASE_URL}/api/uploads/${params.uploadId}/map`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    }
  );
  return handleIngestionResponse<MapLanded>(response);
}

// =============================================================================
// History / templates
// =============================================================================

export async function listUploads(): Promise<UploadsListResponse> {
  const response = await fetch(`${API_BASE_URL}/api/uploads`, {
    headers: createHeaders(),
  });
  return handleResponse<UploadsListResponse>(response);
}

export async function listUploadMappings(): Promise<UploadMappingsListResponse> {
  const response = await fetch(`${API_BASE_URL}/api/upload-mappings`, {
    headers: createHeaders(),
  });
  return handleResponse<UploadMappingsListResponse>(response);
}

// =============================================================================
// Manual spend
// =============================================================================

export async function listManualSpend(): Promise<ManualSpendListResponse> {
  const response = await fetch(`${API_BASE_URL}/api/manual-spend`, {
    headers: createHeaders(),
  });
  return handleResponse<ManualSpendListResponse>(response);
}

export async function createManualSpendEntry(
  input: ManualSpendInput
): Promise<ManualSpendEntryRecord> {
  const response = await fetch(`${API_BASE_URL}/api/manual-spend`, {
    method: 'POST',
    headers: createHeaders(),
    body: JSON.stringify(input),
  });
  return handleIngestionResponse<ManualSpendEntryRecord>(response);
}

export async function updateManualSpendEntry(
  id: string,
  input: ManualSpendInput
): Promise<ManualSpendEntryRecord> {
  const response = await fetch(`${API_BASE_URL}/api/manual-spend/${id}`, {
    method: 'PUT',
    headers: createHeaders(),
    body: JSON.stringify(input),
  });
  return handleIngestionResponse<ManualSpendEntryRecord>(response);
}

export async function deleteManualSpendEntry(id: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/manual-spend/${id}`, {
    method: 'DELETE',
    headers: createHeaders(),
  });
  await handleResponse(response);
}
